import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href: string;
  current?: boolean;
}

const BreadcrumbNavigation: React.FC = () => {
  const location = useLocation();
  
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const path = location.pathname;
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', href: '/' }
    ];

    switch (path) {
      case '/pomodoro':
        breadcrumbs.push({ label: 'Pomodoro Timer', href: '/pomodoro', current: true });
        break;
      case '/kanban':
        breadcrumbs.push({ label: 'Kanban Board', href: '/kanban', current: true });
        break;
      case '/eisenhower':
        breadcrumbs.push({ label: 'Eisenhower Matrix', href: '/eisenhower', current: true });
        break;
      case '/tasks':
        breadcrumbs.push({ label: 'Task Management', href: '/tasks', current: true });
        break;
      default:
        return [];
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null;
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.label,
      "item": `https://freepomodorotimer.com${breadcrumb.href}`
    }))
  };

  return (
    <>
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      
      <nav aria-label="Breadcrumb" className="mb-6">
        <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
          {breadcrumbs.map((breadcrumb, index) => (
            <li key={breadcrumb.href} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground/50" />
              )}
              {breadcrumb.current ? (
                <span 
                  className="text-foreground font-medium"
                  aria-current="page"
                >
                  {breadcrumb.label}
                </span>
              ) : (
                <Link 
                  to={breadcrumb.href}
                  className="hover:text-foreground transition-colors duration-200"
                  aria-label={`Go to ${breadcrumb.label}`}
                >
                  {index === 0 && <Home className="w-4 h-4 inline mr-1" />}
                  {breadcrumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
};

export default BreadcrumbNavigation;